import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { currentRole, isLivyAdmin, livyUnavailableMessage } from 'src/components/livy/access';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CONSTANTS } from '@livable/saas-react-components';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { LivyPanel } from 'src/components/livy/LivyPanel';
import { LivyAvatar } from 'src/components/livy/LivyAvatar';
import { selectedCompanyName, useLivyChat } from 'src/components/livy/useLivyChat';
import { livyConfig } from 'src/lib/livyTransport';
import { LivyHistory } from 'src/components/livy/LivyHistory';
import { useSearchParams } from 'react-router-dom';
import { LivyTheme } from 'src/components/livy/LivyTheme';
import { freshToken, tokenForCompany } from 'src/lib/livyApi';
import { LivyPreviewBanner } from 'src/components/livy/LivyPreviewBanner';
import { LivySurfacePicker, SURFACE_STARTERS, runSurface, surfaceFromRun } from 'src/components/livy/LivySurfacePicker';
const STARTERS = ['Which properties have allocations pending completion this cycle?', 'What is our recovery rate by property this month?', 'Which utilities jumped most vs last cycle?', 'What is blocking billing from closing?'];

/**
 * Full-page Livy. Admin-gated for the initial rollout, every surface ships behind
 * LivableAdmin/LivableSuperAdmin and un-gates progressively.
 */
function LivyPageBody() {
  const companyLabel = selectedCompanyName();
  const isAdmin = isLivyAdmin(currentRole());
  const context = useMemo(() => ({
    route: '/livy'
  }), []);
  const _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 2),
    searchParams = _useSearchParams2[0],
    setSearchParams = _useSearchParams2[1];
  const _useState = useState('admin'),
    _useState2 = _slicedToArray(_useState, 2),
    view = _useState2[0],
    setView = _useState2[1];
  // freshToken, not the raw store: a streaming turn never passes through the app's refresh
  // interceptor. Customer preview also moves the token onto the company the app has selected.
  const preview = view === 'pm';
  const getToken = useCallback(() => preview ? tokenForCompany() : freshToken(), [preview]);
  // A resumed thread keeps the surface it ran on (the drawer's staff surface is not admin) until
  // the view changes or a new chat starts.
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    resumedOn = _useState4[0],
    setResumedOn = _useState4[1];
  const surface = resumedOn !== null && resumedOn !== void 0 ? resumedOn : view;
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    historyOpen = _useState6[0],
    setHistoryOpen = _useState6[1];
  const config = livyConfig();
  const _useLivyChat = useLivyChat({
      // A null config means Livy is not deployed in this environment; the gate below renders first.
      config: config !== null && config !== void 0 ? config : {
        endpoint: '',
        region: '',
        identityPoolId: '',
        userPoolId: ''
      },
      getToken,
      context,
      surface
    }),
    messages = _useLivyChat.messages,
    streaming = _useLivyChat.streaming,
    error = _useLivyChat.error,
    send = _useLivyChat.send,
    stop = _useLivyChat.stop,
    reset = _useLivyChat.reset,
    retry = _useLivyChat.retry,
    resume = _useLivyChat.resume,
    conversationId = _useLivyChat.conversationId;

  // The command palette opens entity results as /livy?ask=… because nothing else deep-links a
  // record. Sent once, then stripped, so a refresh does not re-ask.
  const asked = useRef(false);
  useEffect(() => {
    const ask = searchParams.get('ask');
    // Authorisation before the request, not after the render. This effect runs above the guard
    // below, so without the role check a non-admin following a deep link fired a real turn at the
    // proxy while the page told them Livy was unavailable.
    if (!ask || asked.current || !config || !isAdmin) return;
    asked.current = true;
    setSearchParams({}, {
      replace: true
    });
    send(ask);
  }, [searchParams, setSearchParams, send, config, isAdmin]);
  if (!isAdmin || !config) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        p: '24px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1"
    }, livyUnavailableMessage));
  }
  return (
    /*#__PURE__*/
    // The shell reserves PAGE_BOTTOM_PADDING under every page, which on a chat surface is dead
    // space between the composer and the window. Grow into it and pull the margin back, so the
    // transcript uses the full height instead of ending 80px short of the bottom.
    React.createElement(Stack, {
      sx: {
        height: "calc(100% + ".concat(CONSTANTS.PAGE_BOTTOM_PADDING, "px)"),
        mb: "-".concat(CONSTANTS.PAGE_BOTTOM_PADDING, "px"),
        minHeight: 0
      }
    }, /*#__PURE__*/React.createElement(Paper, {
      variant: "outlined",
      sx: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        borderWidth: '0 0 0 1px'
      }
    }, /*#__PURE__*/React.createElement(Box, {
      sx: {
        px: '16px',
        py: '12px',
        borderBottom: 1,
        borderColor: 'divider'
      }
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      alignItems: "center",
      justifyContent: "space-between",
      spacing: "12px"
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "10px",
      alignItems: "center",
      sx: {
        minWidth: 0
      }
    }, /*#__PURE__*/React.createElement(LivyAvatar, {
      size: 30,
      thinking: streaming
    }), /*#__PURE__*/React.createElement(Box, {
      sx: {
        minWidth: 0
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: '-.01em',
        lineHeight: 1.25
      }
    }, "Livy"), companyLabel && /*#__PURE__*/React.createElement(Typography, {
      noWrap: true,
      sx: {
        fontSize: 11.5,
        color: 'text.secondary'
      }
    }, companyLabel))), /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "8px",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => setHistoryOpen(true)
      // Resuming mid-stream replaces the transcript while the live stream keeps writing
      // into it, so the old conversation's tokens land in the resumed one.
      ,
      disabled: streaming,
      sx: {
        textTransform: 'none',
        fontSize: 12.5,
        minWidth: 0,
        px: '10px'
      }
    }, "History"), /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => {
        setResumedOn(null);
        reset();
      },
      disabled: messages.length === 0 || streaming,
      sx: {
        textTransform: 'none',
        fontSize: 12.5,
        minWidth: 0,
        px: '10px'
      }
    }, "New chat"), /*#__PURE__*/React.createElement(LivySurfacePicker, {
      value: view,
      onChange: next => {
        setView(next);
        setResumedOn(null);
        // A new surface is a new conversation: carrying admin answers into a customer
        // preview would show a customer things their surface cannot reach.
        reset();
      }
    })))), view === 'pm' && /*#__PURE__*/React.createElement(LivyPreviewBanner, {
      companyLabel: companyLabel
    }), /*#__PURE__*/React.createElement(LivyPanel, {
      conversationId: conversationId,
      surface: surface,
      messages: messages,
      streaming: streaming,
      error: error,
      companyLabel: companyLabel,
      starters: view === 'admin' ? STARTERS : SURFACE_STARTERS[view],
      reach: view === 'marketing' ? 'knowledge' : 'account',
      showScope: false,
      onSend: send,
      onStop: stop,
      onRetry: retry
    })), /*#__PURE__*/React.createElement(LivyHistory, {
      open: historyOpen,
      onClose: () => setHistoryOpen(false),
      onResume: (id, history, ran) => {
        // Back on the surface it ran on: an admin thread under the customer preview would show
        // reach that surface lacks, and a preview resumed as admin would lose its scope.
        setView(surfaceFromRun(ran));
        setResumedOn(runSurface(ran));
        resume(id, history);
      }
    }))
  );
}

/** Ripples off inside Livy: see LivyTheme. */
export function LivyPage() {
  return /*#__PURE__*/React.createElement(LivyTheme, null, /*#__PURE__*/React.createElement(LivyPageBody, null));
}